/**
 * ADR-0014 Phase 4 — cancellable idle scheduler for boot/deferred work.
 * Navigation (hashchange) bumps generation so stale surface work does not run.
 *
 * Durable tasks (e.g. lazy Phase-2) are re-scheduled after cancel until they complete,
 * so hash navigation cannot leave deferred domains empty forever.
 *
 * Reschedule is debounced so rapid hashchanges do not thrash the main thread
 * (dashboard felt blocked / "something running in background").
 */

let generation = 0;
const pendingIdleIds = new Set();
const pendingTimeoutIds = new Set();
let hashListenerBound = false;
let durableRescheduleTimer = null;

/** @type {null | { task: () => void|Promise<void>, label?: string, inFlight: boolean, done: boolean }} */
let durableBootTask = null;

const DURABLE_RESCHEDULE_DEBOUNCE_MS = 400;
/** Prefer true idle; only force after this long so POS/dashboard paint stays smooth. */
const DURABLE_IDLE_TIMEOUT_MS = 4500;

export function getBootIdleGeneration() {
  return generation;
}

/**
 * Register boot work that must eventually run even if hash navigation cancels idle slots.
 * @param {() => void|Promise<void>} task
 * @param {{ label?: string }} [options]
 */
export function registerDurableBootIdleTask(task, options = {}) {
  durableBootTask = {
    task,
    label: options.label || 'durable',
    inFlight: false,
    done: false
  };
}

export function clearDurableBootIdleTask() {
  if (durableBootTask) {
    durableBootTask.done = true;
  }
  durableBootTask = null;
  if (durableRescheduleTimer != null) {
    clearTimeout(durableRescheduleTimer);
    durableRescheduleTimer = null;
  }
}

/**
 * Schedule the registered durable boot task if it is not already done/in-flight.
 * Exported so Phase-2 can kick off immediately after register.
 */
export function scheduleDurableBootTaskNow() {
  scheduleDurableBootTaskIfNeeded({ immediate: true });
}

function scheduleDurableBootTaskIfNeeded(opts = {}) {
  if (!durableBootTask || durableBootTask.done || durableBootTask.inFlight) return;

  const debounce = opts.immediate ? 0 : DURABLE_RESCHEDULE_DEBOUNCE_MS;

  if (durableRescheduleTimer != null) {
    clearTimeout(durableRescheduleTimer);
    durableRescheduleTimer = null;
  }

  const kick = () => {
    durableRescheduleTimer = null;
    if (!durableBootTask || durableBootTask.done || durableBootTask.inFlight) return;

    const entry = durableBootTask;
    scheduleCancellableIdle(
      async () => {
        if (!durableBootTask || durableBootTask !== entry || entry.done || entry.inFlight) return;
        entry.inFlight = true;
        try {
          await entry.task();
          entry.done = true;
          if (durableBootTask === entry) {
            durableBootTask = null;
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `[bootIdle] durable task failed (${entry.label}):`,
              err?.message || err
            );
          }
          entry.done = true;
          if (durableBootTask === entry) {
            durableBootTask = null;
          }
        } finally {
          entry.inFlight = false;
        }
      },
      { timeout: DURABLE_IDLE_TIMEOUT_MS, label: entry.label }
    );
  };

  if (debounce <= 0) {
    kick();
  } else {
    durableRescheduleTimer = setTimeout(kick, debounce);
  }
}

/**
 * Cancel pending idle/timeouts and bump generation.
 * @param {string} [reason]
 * @returns {number} new generation
 */
export function bumpBootIdleGeneration(reason = 'manual') {
  generation += 1;
  cancelPendingBootIdleWork();
  if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined') {
    console.debug(`[bootIdle] generation=${generation} reason=${reason}`);
  }
  // Debounced: avoid Phase-2 + InstantCache warm fighting the dashboard on every hash tick.
  scheduleDurableBootTaskIfNeeded({ immediate: false });
  return generation;
}

export function cancelPendingBootIdleWork() {
  if (typeof cancelIdleCallback === 'function') {
    pendingIdleIds.forEach((id) => {
      try {
        cancelIdleCallback(id);
      } catch {
        /* ignore */
      }
    });
  }
  pendingIdleIds.clear();

  pendingTimeoutIds.forEach((id) => {
    try {
      clearTimeout(id);
    } catch {
      /* ignore */
    }
  });
  pendingTimeoutIds.clear();
}

/**
 * Schedule work that no-ops if generation changed (navigation / surface switch).
 * @param {() => void|Promise<void>} task
 * @param {{ timeout?: number, label?: string }} [options]
 * @returns {{ generation: number, cancel: () => void }}
 */
export function scheduleCancellableIdle(task, options = {}) {
  const timeout = options.timeout ?? 2000;
  const scheduledGen = generation;

  const run = () => {
    if (scheduledGen !== generation) return;
    try {
      void task();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[bootIdle] task failed (${options.label || 'unnamed'}):`, err?.message || err);
      }
    }
  };

  let idleId = null;
  let timeoutId = null;

  if (typeof requestIdleCallback === 'function') {
    idleId = requestIdleCallback(run, { timeout });
    pendingIdleIds.add(idleId);
  } else {
    timeoutId = setTimeout(run, Math.min(timeout, 50));
    pendingTimeoutIds.add(timeoutId);
  }

  return {
    generation: scheduledGen,
    cancel() {
      if (idleId != null && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(idleId);
        pendingIdleIds.delete(idleId);
      }
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        pendingTimeoutIds.delete(timeoutId);
      }
    }
  };
}

/**
 * Bind once: hash navigation cancels stale deferred warm / phase work tokens.
 */
export function ensureBootIdleNavigationCancel() {
  if (typeof window === 'undefined' || hashListenerBound) return;
  hashListenerBound = true;
  window.addEventListener('hashchange', () => {
    bumpBootIdleGeneration('hashchange');
  });
}
